import {Injectable} from '@angular/core';
import {Http, URLSearchParams} from '@angular/http';
import {Subject} from 'rxjs/Subject';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/switchMap';
import {App} from 'ionic-angular';
import {OauthService} from './oauth.service';
import {Search} from '../models/search/search.model';
import {ToolsService} from './tools.service';
import {AppProvider} from "../../core/app/app-provider.service";

@Injectable()
export class BeneficiairesService extends OauthService {

  public loader = new Subject<boolean>();

  constructor(
    protected http: Http,
    protected app: App,
    protected appProvider: AppProvider,
    public readonly tools: ToolsService,
  ) {
    super(http, app, appProvider, tools);
    this.loader.next(true); // init custom loader
  }

  getAll(search: Observable<Search> = null, limit: number = 10, offset: number = null): Observable<any> {
    return search.debounceTime(500)
      .distinctUntilChanged()
      .switchMap(s => this.search(s.term, s.action, s.limit, s.offset, 1)); // just ouvrantDroit
  }

  search(
    search: string,
    action: string,
    limit: number = 10,
    offset: number = null,
    ouvrantDroit: number = null,
    sort = 'nom',
    order: string = 'asc',
    extended: number = null
  ): Observable<any> {
    const url = this.Routing.generate('api_get_beneficiaires', {'_format': 'json'}, true);
    const params = new URLSearchParams();

    if (action !== 'scroll') {
      this.loader.next(true); // enable custom loader
    }

    if (search !== null) {
      params.set('search', search.toString());
    }

    if (offset !== null) {
      params.set('offset', offset.toString());
    }

    if (limit !== null) {
      params.set('limit', limit.toString());
    }

    if (sort !== null) {
      params.set('orderBy', sort.toString());
    }

    if (order !== null) {
      params.set('order', order.toString());
    }

    if (ouvrantDroit !== null) {
      params.set('ouvrantDroit', ouvrantDroit.toString());
    }

    if (extended !== null) {
      params.set('extended', extended.toString());
    }

    return this.http
      .get(url, {search: params, headers: this.updateHeaders()})
      .map(response => {
        this.loader.next(false); // disable custom loader
        return {data: response.json().data, action};
      });
  }

  getBeneficiaire(id): Observable<any> {
    const url = this.Routing.generate('api_get_beneficiaire', {'_format': 'json', 'beneficiaire': id}, true);

    return this.http
      .get(`${url}`, {headers: this.updateHeaders()})
      .map(response => {
        this.loader.next(true); // enable custom loader
        return response.json().data;
      });
  }

  getCategorie(): Observable<any> {
    const param = {'_format': 'json'};

    const url = this.Routing.generate('api_get_categories', param, true);

    return this.http
      .get(`${url}`, {headers: this.updateHeaders()})
      .map(response => {
        this.loader.next(true); // enable custom loader
        return response.json().data;
      });
  }

  getTypeContrats(): Observable<any> {
    const param = {'_format': 'json'};

    const url = this.Routing.generate('api_get_type_contrats', param, true);

    return this.http
      .get(`${url}`, {headers: this.updateHeaders()})
      .map(response => {
        this.loader.next(true); // enable custom loader
        return response.json().data;
      });
  }


  getType(): Observable<any> {
    const param = {'_format': 'json'};

    const url = this.Routing.generate('api_get_types', param, true);

    return this.http
      .get(`${url}`, {headers: this.updateHeaders()})
      .map(response => {
        this.loader.next(true); // enable custom loader
        return response.json().data;
      });
  }

  getAyantDroits(article): Observable<any> {
    const param = {'_format': 'json'};
    param['article'] = article;

    const url = this.Routing.generate('api_get_articles_ayantdroit', param, true);

    return this.http
      .get(`${url}`, {headers: this.updateHeaders()})
      .map(response => {
        this.loader.next(true); // enable custom loader
        return response.json().data;
      });
  }

  exitBeneficiaire(benef, date): Observable<any> {
    const param = {'_format': 'json'};
    param['beneficiaire'] = benef;

    const url = this.Routing.generate('api_patch_beneficiaire', param, true);

    return this.http
      .patch(url, JSON.stringify({dateFin: date}), {headers: this.updateHeaders()})
      .map(response => response.json());
  }

  saveBeneficiaire(benef, id?: number, noAddtionnalFields = false): Observable<any> {

    if (!('ouvrantDroit' in benef) && !noAddtionnalFields) {
      benef.ouvrantDroit = true;
    }

    if (!id) {
      return this.addBeneficiaire(benef);
    } else {
      return this.updateBeneficiaire(benef, id);
    }
  }

  addBeneficiaire(benef): Observable<any> {
    const url = this.Routing.generate('api_post_beneficiaire', {'_format': 'json'}, true);

    return this.http
      .post(url, JSON.stringify(benef), {headers: this.updateHeaders()})
      .map(response => response.json());
  }

  updateBeneficiaire(benef, id: number): Observable<any> {
    const url = this.Routing.generate('api_put_beneficiaire', {'_format': 'json', 'beneficiaire': id}, true);

    return this.http
      .put(url, JSON.stringify(benef), {headers: this.updateHeaders()})
      .map(response => response.json());
  }

  saveAD(ad, id?: number): Observable<any> {
    if (!id) {
      return this.addAD(ad);
    } else {
      return this.updateAD(ad, id);
    }
  }

  addAD(ad): Observable<any> {
    const url = this.Routing.generate('api_post_beneficiaire_ayant_droit', {'_format': 'json'}, true);

    return this.http
      .post(url, JSON.stringify(ad), {headers: this.updateHeaders()})
      .map(response => response.json());
  }

  updateAD(ad, id: number): Observable<any> {
    const url = this.Routing.generate('api_put_beneficiaire_ayant_droit', {'_format': 'json', 'ayantDroit': id}, true);

    return this.http
      .put(url, JSON.stringify(ad), {headers: this.updateHeaders()})
      .map(response => response.json());
  }

  savePhoto(benefId, photo): Observable<any> {
    const url = this.Routing.generate('api_post_beneficiaire_photo', {
      '_format': 'json',
      'beneficiaire': benefId
    }, true);
    const formData = new FormData();
    formData.append('file', photo);
    return this.http
      .post(url, formData, {headers: this.updateHeaders('upload')})
      .map(response => response.json());
  }

  getPhoto(benefId): Observable<any> {
    const url = this.Routing.generate('api_get_beneficiaire_photo', {'_format': 'json', 'beneficiaire': benefId}, true);

    return this.http
      .get(url, {headers: this.updateHeaders()})
      .map(response => {
        // this.loader.next(true); // enable custom loader
        return response.text();
      });
  }
  addPieceJointe(idBenef: number, file: any, nature: number): Observable<any> {
    // Construct form data for the API
    const formData = {
      'file': file,
      'nature': nature
    };

    const url = this.Routing.generate('api_post_beneficiaire_pj', {
      '_format': 'json',
      'beneficiaire': idBenef
    }, true);

    return this.http
      .post(url, formData, { headers: this.updateHeaders() })
      .map(response => response.json());
  }
  updatePieceJointe(idBenef: number, idPieceJointe: number, nature: number, clos: boolean): Observable<any> {
    // Construct form data for the API
    const formData = {
      'clos': clos,
      'nature': nature
    };

    const url = this.Routing.generate('api_put_beneficiaire_pj', {
      '_format': 'json',
      'beneficiaire': idBenef,
      'pieceJointe': idPieceJointe
    }, true);

    return this.http
      .put(url, formData, { headers: this.updateHeaders() })
      .map(response => response.json());
  }
  deletePieceJointe(idBenef: number, idPieceJointe: number): Observable<any> {
    const url = this.Routing.generate('api_delete_beneficiaire_pj', {
      '_format': 'json',
      'beneficiaire': idBenef,
      'pieceJointe': idPieceJointe,
    }, true);
    return this.http
      .delete(url, { headers: this.updateHeaders() })
      .map(response => response.json());
  }
  addPieceJointeRib(idRib: number, file: any): Observable<any> {
    // Construct form data for the API
    const formData = {
      'file': file
    };

    const url = this.Routing.generate('api_post_beneficiaire_pj_rib', {
      '_format': 'json',
      'rib': idRib
    }, true);

    return this.http
      .post(url, formData, { headers: this.updateHeaders() })
      .map(response => response.json());
  }
  deletePieceJointeRib(idRib: number, idPieceJointe: number): Observable<any> {
    const url = this.Routing.generate('api_delete_beneficiaire_pj_rib', {
      '_format': 'json',
      'ribId': idRib,
      'pieceJointeId': idPieceJointe,
    }, true);
    return this.http
      .delete(url, { headers: this.updateHeaders() })
      .map(response => response.json());
  }
  cloturerRib(idBenef: number): Observable<any> {
    const url = this.Routing.generate('api_post_beneficiaire_cloturer_rib', {
      '_format': 'json',
      'beneficiaire': idBenef
    }, true);

    return this.http
      .post(url, {}, { headers: this.updateHeaders() });
  }
  confirmDonneesAD(idOd: number, idAd: number): Observable<any> {
    const url = this.Routing.generate('api_post_beneficiaire_confirmer_donnees_ad', null, true);
    const body = {
      '_format': 'json',
      'ouvrantdroit' : idOd,
      'ayantdroit': idAd
    }

    return this.http
      .post(url, body, {headers: this.updateHeaders()})
      .map(response => response.json());
  }

  addDepartementFavori(departementId): Observable<any> {
    const url = this.Routing.generate('api_post_beneficiaire_departement_favori', {'_format': 'json', dep: departementId }, true);

    return this.http
      .post(url, null, {headers: this.updateHeaders()})
      .map(response => response.json());
  }

  deleteDepartementFavori(departementId): Observable<any> {
    const url = this.Routing.generate('api_delete_beneficiaire_departement_favori', {'_format': 'json', dep: departementId}, true);

    return this.http
      .delete(url, {headers: this.updateHeaders()})
      .map(response => response.json());
  }

  getUserNotification(limit: number, offset: number) {
    const params = {'_format': 'json', 'limit': limit, 'offset': offset}
    const url = this.Routing.generate('api_get_beneficiaire_user_notification', params, true);


    return this.http
      .get(`${url}`, {headers: this.updateHeaders()})
      .map(response => {
        return response.json().data;
      });
  }

}



// WEBPACK FOOTER //
// ./src/shared/services/beneficiaires.service.ts